import { Component, Input, Output, EventEmitter, ViewChild, SimpleChanges, HostListener } from '@angular/core';
import * as d3 from 'd3';
import { Catalog, Zone, Page } from './catalog.service';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { max } from 'd3';
import { environment } from '../environments/environment';

@Component({
  selector: 'cockehsu-catalog',
  templateUrl: './cockehsu-catalog.component.html',
  styleUrls: ['./cockehsu-catalog.component.css']
})
export class CockehsuCatalogComponent {

  private static NEXT_ID = 1;
  private instanceID: number;

  @Input()
  public catalog: Catalog;

  @Output() private loadProgress = new EventEmitter<CockehsuCatalogProgressEvent>();
  @Output() private loadStart = new EventEmitter<CockehsuCatalogProgressEvent>();
  @Output() private loadEnd = new EventEmitter<CockehsuCatalogProgressEvent>();
  @Output() private pageClick = new EventEmitter<number>();

  private loadSource = new Subject<number>();
  private load$ = this.loadSource.asObservable();
  private loadCount = 0;

  @ViewChild('paper')
  private paper;

  private svg; // d3.Selection<>
  private vis; // d3.Selection<>
  private defs; // d3.Selection<>

  private viewInit: boolean;
  private _w: number; // last computed width
  private _h: number; // last computed height
  private firstLoad: boolean;

  private gesturePoint: Point;
  private gestureStartPoint: Point;
  private gestureDirection: number;

  private swipeStartPoint: Point;
  private swipeStartTime: number;
  
  private numPages: number;
  private _pageIndex: number;

  public set pageIndex(v: number) {
    const max = this.numPages;
    if (v > max) {
      v = max;
    } else if (v < 0) {
      v = 0;
    }
    this._pageIndex = v;
  }
  public get pageIndex(): number { return this._pageIndex; }

  private imgdims: Array<Dim>;
  private zoneColor = 'rgb(174, 199, 232)';
  private zoneOpacity = 0.6;
  private yMargin = 5;
  private yOffset;

  private snapGrid: Array<number>;

  private _useShadows: boolean = false;

  @Input()
  public set shadow(t: boolean) {
    this._useShadows = t;
    this.redraw();
  }

  public get shadow(): boolean {
    return this._useShadows;
  }

  private _highlight: boolean = false;

  @Input()
  public set highlight(t: boolean) {
    this._highlight = t;
    this.redraw();
  }

  public get highlight(): boolean {
    return this._highlight;
  }

  private _zoom: boolean = false;

  @Input()
  public set zoom(t: boolean) {
    this._zoom = t;
    this.redraw();
  }

  public get zoom(): boolean {
    return this._zoom;
  }

  private _pagesSpread: number = 2;

  private _multiPages: number = 1;

  @Input()
  public set multiPages(n: number) {
    if (n < 1) {
      return;
    }
    this._multiPages = n;
    if (this.viewInit) {
      this.redraw();
    }
  }

  public get multiPages(): number {
    return this._multiPages;
  }

  private _pageNumbers: boolean = false;

  @Input()
  public set pageNumbers(t: boolean) {
    this._pageNumbers = t;
    if (this.viewInit) {
      this.redraw();
    }
  }

  public get pageNumbers(): boolean {
    return this._pageNumbers;
  }

  private _maxZoom: number = 1.0;

  @Input()
  public set maxZoom(t: number) {
    this._maxZoom = t;
  }

  public get maxZoom(): number {
    return this._maxZoom;
  }

  constructor() {
    this.instanceID = CockehsuCatalogComponent.NEXT_ID;
    CockehsuCatalogComponent.NEXT_ID++;
  }

  ngOnChanges(s: SimpleChanges) {
    if (s['catalog'] !== undefined && this.viewInit) {
      this.numPages = this.catalog.pages.length;
      this.firstLoad = true;
      this.loadSource.next(0);
      const view = this;
      this.imgdims = [];
      let i = 0;
      for (let page of this.catalog.pages) {
        var img = new Image();
        const idx = i;
        img.onload = function () {
          const meow: any = this;
          view.imgdims[idx] = new Dim(meow.width, meow.height);
          view.loadSource.next(1);
        }
        if (page.imgURL === '') {
          view.imgdims[idx] = new Dim(0, 0);
          view.loadSource.next(1);
        } else {
          img.src = page.imgURL;
        }
        i++;
      }
    }
  }

  ngOnInit() {
    this.load$.subscribe((p: number) => {
      if (!this.firstLoad) {
        return;
      }
      this.loadCount += p;
      if (this.loadCount === 0) {
        this.loadStart.emit(new CockehsuCatalogProgressEvent(0, this.numPages));
      } else if (this.loadCount === this.numPages) {
        this.loadProgress.emit(new CockehsuCatalogProgressEvent(this.loadCount, this.numPages));
        this.loadEnd.emit(new CockehsuCatalogProgressEvent(this.loadCount, this.numPages));
        this.loadCount = 0;
        this.firstLoad = false;
        // check all dims. if we had empty images, try to find a common ratio, else 1:1.
        for (let i = 0; i < this.imgdims.length; i++) {
          if (this.imgdims[i].w !== 0 && this.imgdims[i].h !== 0) {
            continue
          }
          for (let j = 0; i < this.imgdims.length; j++) {
            const d = this.imgdims[j];
            if (d.w !== 0 && d.h !== 0) {
              this.imgdims[i].w = d.w;
              this.imgdims[i].h = d.h;
              break;
            }
          }
          if (this.imgdims[i].w === 0 || this.imgdims[i].h === 0) {
            this.imgdims[i].w = 720;
            this.imgdims[i].h = 720;
          }
        }
        this.redraw();
      } else {
        this.loadProgress.emit(new CockehsuCatalogProgressEvent(this.loadCount, this.numPages));
      }
    });
    this.pageIndex = 0;
    this.viewInit = true;

    this.svg = d3.select(this.paper.nativeElement)
      .append('svg:svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('overflow', 'hidden')

    this.defs = this.svg.append('svg:defs');
    this.loadDropShadowFilter(this.defs, 'none');
    this.loadDropShadowFilter(this.defs, 'left');

    const subtlenet2 = this.defs.append('pattern')
      .attr('id', `subtlenet2-${this.instanceID}`)
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 60)
      .attr('height', 60);

    subtlenet2.append('image')
      .attr('width', 60)
      .attr('height', 60)
      .attr('xlink:href', 'assets/subtlenet2.png');

    this.vis = this.svg.append('g');
    this.vis.datum(0);
  }

  public swipe(e: TouchEvent, when: string) {
    const swipePoint: Point = new Point(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
    const swipeTime = new Date().getTime();

    if (when === 'start') {
      this.swipeStartPoint = swipePoint;
      this.swipeStartTime = swipeTime;
    } else if (when === 'end') {
      const swipeDiff = new Point(swipePoint.x - this.swipeStartPoint.x, swipePoint.y - this.swipeStartPoint.y);
      const swipeDuration = swipeTime - this.swipeStartTime;

      if (swipeDuration < 1000
          && Math.abs(swipeDiff.x) > 30
          && Math.abs(swipeDiff.x) > Math.abs(swipeDiff.y * 3)) {
        if (swipeDiff.x < 0) {
              this.nextPage();
            } else {
              this.prevPage();
            }
          } 
      }
    }
  
  
  
  private drawPaper() {
    // Cleanup
    this.vis.selectAll('g.page').remove();
    this.vis.selectAll('clipPath').remove();
    this.vis.selectAll('g.zoom').remove();

    let ne = this.paper.nativeElement;
    const w = ne.clientWidth;
    const h = ne.clientHeight;
    this._w = w;
    this._h = h;

    if (w < 640) {
      this._pagesSpread = 1;
    }

    this.svg.attr('viewBox', `0 0 ${this._w} ${this._h}`);

    let data = this.catalog.pages;
    let pages = this.vis.selectAll('g.page').data(data);
    let color = d3.scaleOrdinal().range(d3.schemeCategory10);

    const pageTextHeight = Math.sqrt(h);
    this.snapGrid = [];

    const view = this;

    const arrow: any = d3.select('div.right-arrow').node();
    let maxDim = new Dim(
      (w / this._multiPages) * (w < 640 ? 1 : 0.5) - arrow.clientWidth,
      h - this.yMargin * 2 - pageTextHeight,
    )
    if (!this._pageNumbers) {
      maxDim.h += pageTextHeight;
    }

    if (maxDim.w <= 0 || maxDim.h <= 0) {
      return;
    }

    let g = pages.enter()
      .append('g')
      .classed('page', true)
      .style('cursor', 'zoom-in');

    if (!this._useShadows) {
      g.append('path').classed('shadow', true).lower();
    }
    let border = g.append('path').classed('border', true).style('opacity', 0);

    if (this._pageNumbers) {
      g.append('text')
        .classed('number', true)
        .attr('text-anchor', 'middle')
        .attr('font-size', `${pageTextHeight}px`)
        .attr('font-weight', 'bold')
        .attr('fill', '#666')
        .text((d) => d.index);
    }

    if (this._zoom) {
      const zg = this.vis.append('g')
        .classed('zoom', true)
        .style('cursor', 'zoom-in')
        .style('opacity', 0);
    }
    let sel = [];
    g.attr('id', (d, i) => `gpage_${this.instanceID}_${i}`)
      .each(function (d: Page, i) {
        let el;
        if (d.imgURL === '') {
          let g = d3.select(this);
          el = g.append('rect')
            .style('fill', `url(#subtlenet2-${view.instanceID})`)
            .style('fill-opacity', 1.0)
          g.append('text')
            .classed('page', true)
            .attr('font-size', '10px')
            .attr('font-weight', 'bold')
            .attr('text-anchor', 'middle')
            .attr('fill', '#ddd')
            .text(d.index);
        } else {
          el = d3.select(this)
            .append('image')
            .attr('xlink:href', d.imgURL)
            // Fuck firefox image dragging
            .on('dragstart', () => {
              d3.event.preventDefault();
            });
        }
        sel[i] = el.node();
      });

    let img = d3.selectAll(sel).data(data);

    if (this.highlight) {
      img.style('cursor', 'pointer')
        .on('click', (d, i) => {
          this.pageClick.emit(i);
          this.moveToPage(i);
        });
    }

    if (this._useShadows) {
      //.style('filter', (d, i) => i%2 === 0 && i !== 0 && i !== numPages-1 ? `url(#dropshadow-left-${this.instanceID})` : `url(#dropshadow-${this.instanceID})`) // This is to clip the shadow on the right pages, to not overlap the left pages
      img.style('filter', `url(#dropshadow-${this.instanceID})`);
    }

    img.each(function (d: any, i: number) {
      const dim = view.imgdims[i];
      // reajust width after loading
      let scaledDim: Dim;
      if (maxDim.ratio() < 1) {
        scaledDim = new Dim(maxDim.w, maxDim.w / dim.ratio());
        view.yOffset = view.yMargin + (maxDim.h - maxDim.w / dim.ratio()) / 2;
      } else {
        scaledDim = new Dim(maxDim.h * dim.ratio(), maxDim.h);
        view.yOffset = view.yMargin;
      }
      d3.select(this)
        .attr('width', () => scaledDim.w)
        .attr('height', () => scaledDim.h);

      let gp = g.filter(`#gpage_${view.instanceID}_${i}`);
      gp.attr('transform', (d) => {
        
        // default single spread page
        let x = ((i + 0.5) * w - (scaledDim.w / 2)) / view._multiPages;

        // double spread pages
        if (view._pagesSpread === 2) {
          x = (1 + i + i % 2) * w * 0.5 / view._multiPages - (scaledDim.w * (i % 2));
          
          if ((i === 0 && (view.catalog.pushes.length === 0 || view._multiPages > 1)) || i === view.numPages - 1) {
            x = x + scaledDim.w * 0.5 * (i % 2 === 0 ? -1 : 1);
          }
        }
        
        view.snapGrid[i] = x;
        return `translate(${x}, ${view.yOffset})`;
      });

      const textPage = gp.select('text.page')
        .attr('font-size', `${scaledDim.h * 0.3}px`)
      if (!textPage.empty()) {
        const tpbb = textPage.node().getBBox();
        textPage
          .attr('x', (scaledDim.w) * 0.5)
          .attr('y', (scaledDim.h) * 0.5)
      }

      // draw fake shadow
      if (!view._useShadows) {
        view.drawPageBorder(gp.select('path.shadow'), i, scaledDim.w, scaledDim.h, 'rgba(51,51,51,0.15)', '6px');
      }

      // position the text
      gp.select('text.number')
        .raise()
        .attr('transform', `translate(${scaledDim.w / 2}, ${scaledDim.h + pageTextHeight + view.yOffset / 2})`);

      // Draw highlight border if wanted
      if (view.highlight) {
        let border = gp.select('path.border');
        view.drawPageBorder(border, i, scaledDim.w, scaledDim.h, '#e41b13', '6px');
        let bordersHover = [border.node()];
        if (i !== 0 && i !== view.numPages - 1 && view._pagesSpread == 2) {
          let idx;
          if (i % 2) {
            idx = i + 1;
          } else {
            idx = i - 1;
          }
          bordersHover.push(g.filter(`#gpage_${view.instanceID}_${idx}`).select('path.border').node());
        }
        const bordersHoverSel = d3.selectAll(bordersHover);
        d3.select(this)
          .on('mouseover', () => {
            bordersHoverSel.raise().transition().duration(300).style('opacity', 1.0);
          })
          .on('mouseout', () => {
            if (i !== view.pageIndex && i + 1 !== view.pageIndex) {
              bordersHoverSel.transition().duration(300).style('opacity', 0.0);
            }
          });
      }

      if (environment.enableProductZone) {
        gp.selectAll('rect')
          .data(d.zones)
          .enter()
          .append('rect')
          .attr('x', (d: Zone) => d.x * scaledDim.w)
          .attr('y', (d: Zone) => d.y * scaledDim.h)
          .attr('width', (d: Zone) => d.w * scaledDim.w)
          .attr('height', (d: Zone) => d.h * scaledDim.h)
          .style('fill', view.zoneColor)
          .style('opacity', 0.0)
          .style('cursor', 'pointer')
          .on('mouseover', function (d) {
            d3.select(this).transition().duration(300).style('opacity', view.zoneOpacity);
            d3.select(this).style("cursor", "pointer");
          })
          .on('mouseout', function (d) {
            d3.select(this).transition().duration(300).style('opacity', 0.0);
          })
          .on('click', function (d) {
            d.actions.forEach(action => {
              if (action.type === 'uri') {
                // Nothing to do
              } else if (action.type === 'zoom') {
                // Nothing to do
              } else if (action.type === 'url') {
                window.open(action.data, '_blank');
              }
            });
          });
      }
    });

    // zoom
    if (view._zoom) {
      this.vis
        .on('mouseover', function () {
          if (!view._zoom) {
            return;
          }
          if (view.zoomSession.level <= 0) {
            return;
          }
          const e = d3.event;
          if (e.target.parentNode.id.indexOf(`gpage_${view.instanceID}`) === 0) {
            const d: Page = <Page>d3.select(e.target).data()[0];
            const zp: Point = view.getZoomPoint(e);
            view.zoomStart(zp.x, zp.y, e.target, d.imgURLHD, d.index - 1);
          }
        });
      this.svg.on('wheel', function () {
        const e = d3.event;
        view.zoomHandler(e);
      })
        .on('dblclick', function () {
          if (!view._zoom) {
            return;
          }
          if (view.zoomSession.level > 0) {
            view.zoomStop();
          } else {
            const e = d3.event;
            if (e.target.parentNode.id.indexOf(`gpage_${view.instanceID}`) === 0) {
              view.zoomSession.level = 1;
              const d: Page = <Page>d3.select(e.target).data()[0];
              const zp: Point = view.getZoomPoint(e);
              view.zoomStart(zp.x, zp.y, e.target, d.imgURLHD, d.index - 1);
            }
          }
        });
    }
  }

  public addZoomLevel(l: number) {
    if (!this.zoomSession.isStarted() && l > 0) {
      this.zoomSession.level = 1.0;
    } else {
      this.zoomSession.level += l;
      this.zoomSession.step = 1 + l;
    }
    if (this.zoomSession.level < 1.0) {
      this.zoomStop();
    } else {
      this.zoomSession.level = this.adjustZoomLevel(this.zoomSession.level);
      let idx = this._pageIndex;
      if (idx === this.numPages) {
        idx -= 1;
      }
      const sel = this.vis.selectAll('g.page').filter(`#gpage_${this.instanceID}_${idx}`);
      const d: Page = sel.data()[0];

      const w: number = +sel.select('image').attr('width');
      const h: number = +sel.select('image').attr('height');
      if (this.zoomSession.isStarted()) {
        if ( this.zoomSession.level < this.maxZoom) {
          this.zoomUpdate();
        }
      } else {
        //let x = 0;
        //if (d.index - 1 === 0 || d.index - 1 == this.numPages - 1) {
        //  x = w /2 ;
        //}
        this.zoomStart(w, h , sel.node(), d.imgURLHD, d.index - 1);
      }
    }
  }

  private zoomHandler(e) {
    if (!this._zoom) {
      return;
    }
    let isStart: boolean = this.zoomSession.level === 0;
    if (isStart && e.deltaY > 0) {
      return;
    } else if (isStart) {
      this.zoomSession.level = 1.0;
    } else {
      if (e.deltaY < 0) {
        this.zoomSession.level += 0.1;
        this.zoomSession.step = 1.1;
      } else if (e.deltaY > 0) {
        this.zoomSession.level += -0.1;
        this.zoomSession.step = 0.9;
      }
    }
    if (this.zoomSession.level < 1) {
      this.zoomStop();
    } else {
      this.zoomSession.level = this.adjustZoomLevel(this.zoomSession.level);
      if (e.target.parentNode.id.indexOf(`gpage_${this.instanceID}`) === 0 && isStart) {
        const d: Page = <Page>d3.select(e.target).data()[0];
        //const zp: Point = this.getZoomPoint(e);
        let idx = this._pageIndex;
        if (idx === this.numPages) {
          idx -= 1;
        }
        const sel = this.vis.selectAll('g.page').filter(`#gpage_${this.instanceID}_${idx}`);
        this.zoomStart(sel.select('image').attr('width'), sel.select('image').attr('height'), e.target, d.imgURLHD, d.index - 1);
      } else {
        if (this.zoomSession.level < this.maxZoom) {    
          const zp: Point = this.getZoomPoint(e);
          this.zoomUpdate(zp);
        }
      }
    }
  }

  public zoomSession: ZoomSession = new ZoomSession(50);

  private getZoomPoint(e: MouseEvent) {
    return new Point(e.clientX, e.clientY);
  }

  private zoomStart(mx: number, my: number, srcNode, imgURL: string, idx: number) {
    if (imgURL === '') {
      return;
    }
    this.zoomSession.start(srcNode, idx);
    this.zoomSession.svgBBox = this.svg.node().getBBox();
    this.zoomSession.level = this.adjustZoomLevel(this.zoomSession.level);
    const r = this.zoomSession.srcNodeBBox;

    const rect = srcNode.getBoundingClientRect();
    const origin = new Point(mx - rect.left, my - rect.top);
    const rorigin = new Point(origin.x / r.width, origin.y / r.height);

    const gZoom = this.vis.select('g.zoom');
    gZoom.selectAll('g').remove();
    const gimgZoom = gZoom.append('g');
    this.vis.select('#zoom-bg').remove();
    const rectZoom = this.vis.append('rect')
      .attr('id', 'zoom-bg')
      .style('fill', `url(#subtlenet2-${this.instanceID})`)
      .style('fill-opacity', '1');
    gZoom.raise();
    const view = this;

    const img = new Image();
    img.src = imgURL;
    img.onload = function () {
      const meow: any = this;
      gZoom
        .style('display', 'block')
        .raise();

      const imgZoom = gimgZoom.append('image')
        .attr('image-rendering', 'optimizeQuality')
        .classed('main', true);
      imgZoom
        .attr('width', meow.width)
        .attr('height', meow.height)
        .attr('xlink:href', imgURL);

      const scale = view.zoomSession.level;
      view.zoomSession.translation.x = view.snapGrid[view.zoomSession.idx] + rorigin.x * r.width - scale * (rorigin.x * meow.width);
      view.zoomSession.translation.y = view.yOffset + rorigin.y * r.height - scale * (rorigin.y * meow.height);

      let rectX: number = (view.zoomSession.idx * view._w);
      if (view._pagesSpread === 2) {
        rectX = (view.zoomSession.idx + view.zoomSession.idx % 2) * 0.5 * view._w;
      }

      let matrix = [scale, 0, 0, scale, view.zoomSession.translation.x, view.zoomSession.translation.y];

      gZoom
        .transition()
        .duration(150)
        .style('opacity', 1.0);

      view.setMatrix(matrix);
      

      rectZoom
        .attr('x', rectX)
        .attr('y', 0)
        .attr('width', view._w)
        .attr('height', view._h)

      if (idx === 0 || idx === view.numPages - 1 || view._pagesSpread === 1) {
        return;
      }
      let adjacentPageIdx: number;
      if (idx % 2 === 0) {
        adjacentPageIdx = idx - 1;
      } else {
        adjacentPageIdx = idx + 1;
      }

      const sel = view.vis.selectAll('g.page').filter(`#gpage_${view.instanceID}_${adjacentPageIdx}`);
      const d: Page = sel.data()[0];
      const imgZoomAdj = gimgZoom.append('image')
        .attr('image-rendering', 'optimizeQuality')
        .attr('width', meow.width)
        .attr('height', meow.height)
        .attr('xlink:href', d.imgURLHD);

      if (adjacentPageIdx > idx) {
        // pos to the right
        imgZoomAdj.attr('transform', `translate(${meow.width}, 0)`)
      } else {
        // pos to the left
        imgZoomAdj.attr('transform', `translate(${-meow.width}, 0)`)
      }
    }

  }

  private adjustZoomLevel(n: number): number {
    if (n >= this._maxZoom) {
      n = this._maxZoom;
    }
    if (n <= 1) {
      n = 1;
    }
    return n;
  }

  private zoomUpdate(point?: Point) {
    if (this.zoomSession.level <= 0) {
      return;
    }

    if (point === undefined) {
      point = this.zoomSession.point;
    } else {
      this.zoomSession.point = point;
    }

    const n1 = this.vis.select('g.zoom g image.main').node();
    if (!n1) {
      return;
    }
    if (this.zoomSession.zoomNodeBBox === undefined) {
      this.zoomSession.zoomNodeBBox = n1.getBBox();
    }
    const scale = this.zoomSession.level;
    const step = this.zoomSession.step;
    
    this.scaleUpdate(scale, step);
  }

  private scaleUpdate(scale: number, step: number) {
    var matrix = this.zoomSession.matrix;
    var gZoom = this.vis.select('g.zoom');
    var gZoomBBox = gZoom.node().getBBox();

    var cx = gZoomBBox.width / 2;
    var cy = gZoomBBox.height / 2;

    matrix[0] = scale;
    matrix[3] = scale;
    matrix[4] += (1 - step) * cx;
    matrix[5] += (1 - step) * cy;

    this.setMatrix(matrix);
  }
  
  private translationUpdate(point?: Point) {
    if (point !== undefined) {
      var matrix = this.zoomSession.matrix;

      matrix[4] += point.x;
      matrix[5] += point.y;

      this.setMatrix(matrix);
    }
  }

  private setMatrix(matrix: number[]) {
    if (!this.zoomSession.isStarted) {
      return;
    }

    this.zoomSession.matrix = matrix;
    this.vis.select('g.zoom')
      .attr('transform', `translate(${matrix[4]}, ${matrix[5]}) scale(${matrix[0]}, ${matrix[3]})`);
  }

  private getPointDiff(a: Point,  b: Point) {
    return new Point(b.x - a.x, b.y - a.y);
  }

  private getMousePosition(e: MouseEvent) {
    var ctm = document.getElementsByTagName('svg')[0].getScreenCTM();
    return new Point((e.clientX - ctm.e) / ctm.a, (e.clientY - ctm.f) / ctm.d);
  }
  
  private zoomStop() {
    this.zoomSession.stop();
    this.vis.select('#zoom-bg').remove();
    this.vis.select('g.zoom')
      .transition()
      .on('end', () => {
        this.vis.select('g.zoom').style('display', 'none');
      })
      .duration(150)
      .style('opacity', 0)
    this.vis.select(`#zoomClip${this.instanceID} rect`)
      .transition()
      .duration(150)
      .attr('width', 0)
      .attr('height', 0)
  }

  private drawPageBorder(pathSel, idx: number, w: number, h: number, color: string, strokewidth: string) {
    const path = d3.path();
    if (idx === 0 || idx === this.numPages - 1 || this._pagesSpread == 1) {
      path.moveTo(0, 0);
      path.lineTo(w, 0);
      path.lineTo(w, h);
      path.lineTo(0, h);
      path.lineTo(0, 0);
      path.closePath();
    } else if (idx % this._pagesSpread === 0) {
      path.moveTo(0, 0);
      path.lineTo(w, 0);
      path.lineTo(w, h);
      path.lineTo(0, h);
    } else if (idx % this._pagesSpread === 1) {
      path.moveTo(w, 0);
      path.lineTo(0, 0);
      path.lineTo(0, h);
      path.lineTo(w, h);
    } else {
      path.moveTo(0, 0);
      path.lineTo(w, 0);
      path.moveTo(w, h);
      path.lineTo(0, h);
    }
    pathSel.attr('d', path)
      .style('stroke', color)
      .style('stroke-width', strokewidth)
      .style('stroke-linejoin', 'round')
      .style('fill', 'none')
      .style('fill-opacity', 0);
  }

  @HostListener('window:resize', ['$event'])
  private onResize(event: Event) {
    this.redraw();
  }

  public redraw(): void {
    if (!this.viewInit) {
      return;
    }
    const w = this.paper.nativeElement.clientWidth;
    const h = this.paper.nativeElement.clientHeight;
    if ((h === 0 || h === this._h) && (w === 0 || w === this._w)) {
      return;
    }
    this.drawPaper();
    this.moveToSelectedPage();
  }

  private moveToSelectedPage() {
    const t = d3.transition('').duration(300);
    this.moveToSelectedPageWithTransition(t);
  }

  private moveToSelectedPageWithTransition(transition: any) {
    this.zoomStop();
    const ne = this.paper.nativeElement;

    let x_tmp = ((this.pageIndex) * -ne.clientWidth) / this._multiPages;

    if (this._pagesSpread === 2) {
      x_tmp = (this.pageIndex * -ne.clientWidth * 0.5) / this._multiPages + ne.clientWidth * 0.5 * (this._multiPages - 1) / this._multiPages;
    }

    const x = x_tmp;

    this.moveTo(x, transition);
    this.highlightPage(this.pageIndex);
  }

  private highlightPage(idx: number) {
    let pages = this.vis.selectAll('g.page');
    pages.selectAll('path.border').raise().transition().duration(300).style('opacity', 0.0);
    pages.filter(`#gpage_${this.instanceID}_${idx}`).select('path.border').transition().duration(300).style('opacity', 1.0);
    if (idx !== 0 || idx !== this.numPages - 1) {
      pages.filter(`#gpage_${this.instanceID}_${idx - 1}`).select('path.border').transition().duration(300).style('opacity', 1.0);
    }
  }

  private moveTo(x: number, transition: any) {
    this.vis.datum(x);
    if (transition !== undefined) {
      this.vis.transition(transition).attr('transform', (d) => `translate(${d},0)`);
    } else {
      this.vis.attr('transform', (d) => `translate(${d},0)`);
    }
  }

  private moveBy(dx: number, transition: any) {
    this.moveTo(this.vis.datum() + dx, transition);
  }

  public moveToPage(idx: number) {
    this.pageIndex = idx + (idx % this._pagesSpread);
    this.moveToSelectedPage();
  }

  public prevPage() {
    if (this.isFirstPage()) {
      return;
    }
    this.moveToPage(this.pageIndex - this._pagesSpread);
  }

  public nextPage() {
    if (this.isLastPage()) {
      return;
    }
    this.moveToPage(this.pageIndex + this._pagesSpread);
  }

  public moveToFirstPage() {
    this.moveToPage(0);
  }

  public moveToLastPage() {
    this.moveToPage(this.numPages + 1);
  }

  private isFirstPage(): boolean {
    return this.pageIndex <= 0;
  }

  private isLastPage(): boolean {
    if (this.catalog === undefined) {
      return false;
    }
    return this.pageIndex >= this.numPages - 1;
  }

  private gestureStart(event: MouseEvent) {
    if (this.zoomSession.isStarted()) {
      return;
    }
    this.gesturePoint = new Point(event.screenX, event.screenY);
    this.gestureStartPoint = this.gesturePoint;
    this.gestureDirection = 0;
  }

  @HostListener('window:mousedown', ['$event'])
  private onMouseDown(e: MouseEvent) {
    this.zoomSession.startMouseDown(this.getMousePosition(e));
  }

  @HostListener('window:mousemove', ['$event'])
  private onMouseMove(e: MouseEvent) {
    if (this.zoomSession.isStarted() && this.zoomSession.mousedown) {
      let inCatalog = false;
      let node: any = e.target;
      this.paper.nativeElement;
      while (node) {
        node = node.parentNode;
        if (node === this.paper.nativeElement) {
          inCatalog = true;
          break;
        }
      }
      if (inCatalog) {
        let prev = this.zoomSession.point;
        let mp = this.getMousePosition(e);
        this.zoomSession.point = mp;
        let diff = this.getPointDiff(prev, mp);
        this.translationUpdate(diff);
      }
    }
    this.gestureUpdate(e);
  }

  private gestureUpdate(event: MouseEvent) {
    if (this.gesturePoint === undefined) {
      return;
    }
    const d = event.screenX - this.gesturePoint.x;
    const p = new Point(event.screenX, event.screenY);
    this.gestureDirection = p.direction(this.gesturePoint);
    this.gesturePoint = p;
    this.moveBy(d, undefined);
  }

  @HostListener('window:mouseup', ['$event'])
  private gestureEnd(event: MouseEvent) {
    this.zoomSession.endMouseDown();

    if (this.gesturePoint === undefined) {
      return;
    }
    const d = this.gestureDirection;
    const dst = Math.abs(this.gesturePoint.x - this.gestureStartPoint.x);
    this.gesturePoint = undefined;
    this.gestureStartPoint = undefined;
    this.gestureDirection = 0;
    const pageIndex = this.pageIndex;
    if (d !== undefined && d !== 0 && dst >= 50) {
      this.pageIndex = this.pageIndex - 2 * d * this._multiPages;
    }
    if (this.pageIndex !== pageIndex) {
      const t = d3.transition('')
        .duration(300 * this._multiPages)
        .ease(d3.easeCubicOut);
      this.moveToSelectedPageWithTransition(t);
    } else {
      this.moveToSelectedPage();
    }
  }

  private loadDropShadowFilter(defs, clip: string) {
    const filter = defs.append('filter')
      .attr('height', '130%');

    let dev = 3, slope = 0.4;
    if (clip === 'left') {
      filter.attr('id', `dropshadow-left-${this.instanceID}`)
        .attr('x', '0%');
    } else if (clip === 'none') {
      filter.attr('id', `dropshadow-${this.instanceID}`)
        .attr('width', '130%');
    }

    filter.append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', dev);

    filter.append('feComponentTransfer')
      .append('feFuncA')
      .attr('type', 'linear')
      .attr('slope', slope);

    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode');
    feMerge.append('feMergeNode')
      .attr('in', 'SourceGraphic');
  }

}

class Point {
  constructor(public x: number, public y: number) { }
  public direction(p: Point): number { return p.x < this.x ? 1 : -1; }
}

class Dim {
  constructor(public w: number, public h: number) { }
  public ratio(): number { return this.w / this.h; }
}

class ZoomSession {
  public level: number = 0;
  public matrix = [];
  public step: number = 0;
  public srcNode = undefined;
  public svgBBox = undefined;
  public srcNodeBBox = undefined;
  public zoomNodeBBox = undefined;
  public mousedown: boolean = false;
  public idx: number = -1;
  public point: Point;
  public translation: Point;

  constructor(public radiusStep: number) { }

  public isStarted(): boolean {
    return this.srcNode !== undefined;
  }

  public start(node, idx: number) {
    this.srcNode = node;
    this.srcNodeBBox = node.getBBox();
    this.idx = idx;
    this.translation = new Point(0, 0);
    this.matrix = [1,0,0,1,0,0];
    this.step = 1;
  }

  public startMouseDown(p?: Point) {
    this.mousedown = true;
    this.point = p;
  }

  public endMouseDown() {
    this.mousedown = false;
    this.point = undefined;
  }

  public stop() {
    this.srcNode = undefined;
    this.srcNodeBBox = undefined;
    this.zoomNodeBBox = undefined;
    this.point = undefined;
    this.idx = -1;
    this.level = 0;
    this.mousedown = false;
    this.translation = new Point(0, 0);
    this.matrix = undefined;
    this.step = 1;
  }
}

export class CockehsuCatalogProgressEvent {
  constructor(public currentPages: number, public totalPages: number) {}

  public progress(): number {
    return this.currentPages/this.totalPages;
  }
}



// WEBPACK FOOTER //
// ./src/app/cockehsu-catalog.component.ts